import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Button from '../components/Button';
import { formatDate } from '../utils/dateUtils'; // Assume you have a date formatting utility

// Add a safe number formatting helper
const formatAmount = (amount) => {
  if (amount == null || isNaN(amount)) return '0';
  return Number(amount).toLocaleString();
};

function ManageQuotes({ lang }) {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, "quotes"), orderBy("timestamp", "desc"));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const quotesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuotes(quotesData);
    }, (error) => {
      console.error("Error fetching quotes:", error);
    });

    return () => unsubscribe();
  }, []);

  const loadQuotes = () => {
    const savedQuotes = JSON.parse(localStorage.getItem('quoteSubmissions') || '[]');
    setQuotes(savedQuotes.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      const quoteRef = doc(db, "quotes", quoteId);
      await updateDoc(quoteRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error("Error updating quote:", error);
    }
  };

  const handleGenerateQuote = (quote) => {
    // Example: generate PDF or finalize the quote
    // const doc = new jsPDF();
    // doc.text(`Quote for ${quote.name}`, 10, 10);
    // doc.save(`quote-${quote.id}.pdf`);
    alert(lang === "th" ? "ใบเสนอราคาถูกสร้างแล้ว" : "Quote generated.");
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = quote.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quote.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quote.phone?.includes(searchTerm);
    if (filter === 'all') return matchesSearch;
    return quote.status === filter && matchesSearch;
  });

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <Helmet>
        <title>{lang === "th" ? "จัดการคำขอ - โ��โก้ JCB ให้เช่า" : "Manage Quotes - Koko JCB Rental"}</title>
      </Helmet>

      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <h1 className="text-2xl font-bold mb-4 sm:mb-0">
          {lang === "th" ? "จัดการคำขอใบเสนอราคา" : "Manage Quote Requests"}
        </h1>
        <Button
          onClick={() => navigate('/admin')}
          className="bg-yellow-500 hover:bg-yellow-600 text-white"
        >
          {lang === "th" ? "กลับสู่แดชบอร์ด" : "Back to Dashboard"}
        </Button>
      </div>

      <div className="flex gap-4 mb-4">
        {/* Search UI */}
        <input
          type="text"
          placeholder={lang === "th" ? "ค้นหา..." : "Search..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border p-2 rounded"
        />
        <button onClick={loadQuotes} className="bg-gray-300 px-3 py-2 rounded">
          {lang === "th" ? "โหลดข้อมูลเก่า" : "Load Old Data"}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <input
          type="text"
          placeholder={lang === "th" ? "ค้นหา..." : "Search..."}
          className="border rounded-md px-4 py-2 flex-grow"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="border rounded-md px-4 py-2"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="all">{lang === "th" ? "ทั้งหมด" : "All"}</option>
          <option value="pending">{lang === "th" ? "รอดำเนินการ" : "Pending"}</option>
          <option value="contacted">{lang === "th" ? "ติดต่อแล้ว" : "Contacted"}</option>
          <option value="completed">{lang === "th" ? "เสร็จสิ้น" : "Completed"}</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg shadow">
          {/* ...table headers... */}
          <tbody>
            {filteredQuotes.map((quote) => (
              <tr key={quote.id} className="border-b hover:bg-gray-50">
                <td className="px-6 py-4">{new Date(quote.timestamp).toLocaleDateString()}</td>
                <td className="px-6 py-4">{quote.name}</td>
                <td className="px-6 py-4">{quote.phone}</td>
                <td className="px-6 py-4">{quote.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${formatAmount(quote.amount)}
                </td>
                <td className="px-6 py-4">
                  <select
                    value={quote.status || 'pending'}
                    onChange={(e) => updateQuoteStatus(quote.id, e.target.value)}
                    className="border rounded px-2 py-1"
                  >
                    <option value="pending">{lang === "th" ? "รอดำเนินการ" : "Pending"}</option>
                    <option value="contacted">{lang === "th" ? "ติดต่อแล้ว" : "Contacted"}</option>
                    <option value="completed">{lang === "th" ? "เสร็จสิ้น" : "Completed"}</option>
                  </select>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleGenerateQuote(quote)}
                    className="bg-green-500 text-white px-3 py-2 rounded-md mr-2"
                  >
                    {lang === "th" ? "สร้างเอกสารใบเสนอราคา" : "Generate Quote"}
                  </button>
                  <button
                    onClick={() => navigate('/admin/invoice-dashboard')}
                    className="bg-blue-500 text-white px-3 py-2 rounded-md"
                  >
                    {lang === "th" ? "ไปที่แดชบอร์ดใบแจ้งหนี้" : "Go to Invoice Dashboard"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-6">
        <Button
          onClick={() => navigate('/admin/invoice-dashboard')}
          className="bg-yellow-500 hover:bg-yellow-600 text-white"
        >
          {lang === "th" ? "ไปที่แดชบอร์ดใบแจ้งหนี้" : "Go to Invoice Dashboard"}
        </Button>
      </div>
    </div>
  );
}

export default ManageQuotes;